import { useState } from "react"
import { useQuery, useMutation } from "urql"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useDebouncedValue } from "@/hooks/use-debounced-value"
import { useCursorPagination } from "@/hooks/use-cursor-pagination"
import { Plus, Trash2 } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { PageHeader } from "@/components/shared/page-header"
import { DataTable, type Column } from "@/components/shared/data-table"
import { StatusBadge } from "@/components/shared/status-badge"
import { TimeAgo } from "@/components/shared/time-ago"
import { ConfirmDialog } from "@/components/shared/confirm-dialog"
import { BulkActionBar } from "@/components/shared/bulk-action-bar"
import { EndpointForm } from "./components/endpoint-form"
import { EndpointsQuery, DeleteEndpointMutation, BulkDeleteEndpointsMutation, ApplicationsQuery } from "@/lib/graphql/outbound"
import type { ResultOf } from "@/lib/graphql/types"
import { Badge } from "@/components/ui/badge"
import { destinationTypeLabels, type DestinationTypeValue } from "@/lib/schemas"
import type { EndpointStatus } from "@/lib/generated/graphql"

type EndpointItem = ResultOf<typeof EndpointsQuery>["endpoints"]["nodes"][number]

const LIMIT = 25

export function EndpointsPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchInput, setSearchInput] = useState("")
  const debouncedSearch = useDebouncedValue(searchInput)

  const applicationId = searchParams.get("applicationId") ?? undefined
  const status = (searchParams.get("status") as EndpointStatus) || undefined
  const search = debouncedSearch || undefined
  const pagination = useCursorPagination(LIMIT, [applicationId, status, search])

  const [applications] = useQuery({ query: ApplicationsQuery })
  const [result, reexecute] = useQuery({
    query: EndpointsQuery,
    variables: { applicationId, status, search, after: pagination.after, first: pagination.first },
  })

  const setFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) params.set(key, value)
    else params.delete(key)
    setSearchParams(params, { replace: true })
  }

  const [, deleteEndpoint] = useMutation(DeleteEndpointMutation)
  const [, bulkDeleteEndpoints] = useMutation(BulkDeleteEndpointsMutation)
  const [formOpen, setFormOpen] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [bulkDeleting, setBulkDeleting] = useState(false)

  const columns: Column<EndpointItem>[] = [
    {
      header: "Type",
      cell: (row) => (
        <Badge variant={row.type === "HTTP" ? "secondary" : "outline"} className="text-2xs">
          {destinationTypeLabels[row.type as DestinationTypeValue] ?? row.type}
        </Badge>
      ),
    },
    {
      header: "URL",
      cell: (row) => (
        <code className="text-xs font-mono text-muted-foreground max-w-75 truncate block">
          {row.type === "HTTP" ? row.url : "—"}
        </code>
      ),
    },
    {
      header: "Application",
      cell: (row) => <span className="text-xs">{row.application.name}</span>,
    },
    {
      header: "Health",
      cell: (row) => {
        if (row.disabledReason) {
          return (
            <Badge variant="destructive" className="text-2xs">
              {row.disabledReason === "unhealthy" ? "Auto-disabled: unhealthy" : `Disabled: ${row.disabledReason}`}
            </Badge>
          )
        }
        const score = row.healthScore
        const color = score >= 0.8 ? "bg-emerald-500" : score >= 0.4 ? "bg-amber-500" : "bg-red-500"
        return (
          <div className="flex items-center gap-1.5">
            <span className={`inline-block h-2 w-2 rounded-full ${color}`} />
            <span className="text-xs tabular-nums">{Math.round(score * 100)}%</span>
          </div>
        )
      },
    },
    {
      header: "Status",
      cell: (row) => <StatusBadge status={row.status} />,
    },
    {
      header: "Timeout",
      cell: (row) => <span className="text-xs tabular-nums">{row.timeoutMs}ms</span>,
    },
    {
      header: "Rate Limit",
      cell: (row) => (
        <span className="text-xs text-muted-foreground tabular-nums">
          {row.rateLimitRps > 0 ? `${row.rateLimitRps} rps` : "unlimited"}
        </span>
      ),
    },
    {
      header: "Auth",
      cell: (row) => (
        <span className="text-xs text-muted-foreground">
          {row.authType === "NONE" ? "—" : row.authType.toLowerCase()}
        </span>
      ),
    },
    {
      header: "Created",
      cell: (row) => <TimeAgo date={row.createdAt} />,
    },
    {
      header: "",
      cell: (row) => (
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive"
          aria-label="Delete"
          onClick={(e) => {
            e.stopPropagation()
            setDeleteId(row.id)
          }}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      ),
      className: "w-12 text-right",
    },
  ]

  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    const res = await deleteEndpoint({ id: deleteId })
    setDeleting(false)
    if (res.error) { toast.error(res.error.message); return }
    toast.success("Endpoint deleted")
    setDeleteId(null)
    reexecute({ requestPolicy: "network-only" })
  }

  const handleBulkDelete = async () => {
    setBulkDeleting(true)
    const res = await bulkDeleteEndpoints({ ids: Array.from(selectedKeys) })
    setBulkDeleting(false)
    if (res.error) { toast.error(res.error.message); return }
    toast.success(`Deleted ${res.data?.bulkDeleteEndpoints ?? 0} endpoints`)
    setSelectedKeys(new Set())
    setBulkDeleteOpen(false)
    reexecute({ requestPolicy: "network-only" })
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Endpoints" description="Outbound webhook delivery targets">
        <Button onClick={() => setFormOpen(true)} size="sm" className="shadow-primary-glow transition-shadow">
          <Plus className="h-4 w-4 mr-1.5" />
          Create Endpoint
        </Button>
      </PageHeader>

      <div className="flex items-center gap-3 rounded-lg glass-card p-3">
        <Select
          value={applicationId ?? "all"}
          onValueChange={(v) => setFilter("applicationId", v === "all" ? "" : v)}
        >
          <SelectTrigger className="w-45">
            <SelectValue placeholder="All applications" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All applications</SelectItem>
            {applications.data?.applications.nodes.map((app) => (
              <SelectItem key={app.id} value={app.id}>{app.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={status ?? "all"}
          onValueChange={(v) => setFilter("status", v === "all" ? "" : v)}
        >
          <SelectTrigger className="w-35">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="ACTIVE">Active</SelectItem>
            <SelectItem value="INACTIVE">Inactive</SelectItem>
          </SelectContent>
        </Select>

        <Input
          placeholder="Search endpoints..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-50 text-sm"
        />
      </div>

      <BulkActionBar count={selectedKeys.size} onClear={() => setSelectedKeys(new Set())}>
        <Button variant="destructive" size="sm" onClick={() => setBulkDeleteOpen(true)}>
          <Trash2 className="h-3.5 w-3.5 mr-1.5" />
          Delete
        </Button>
      </BulkActionBar>

      <DataTable
        columns={columns}
        data={result.data?.endpoints.nodes ?? []}
        keyFn={(row) => row.id}
        cursorInfo={result.data?.endpoints.pageInfo}
        cursorCallbacks={pagination}
        pageSize={LIMIT}
        loading={result.fetching}
        emptyTitle="No endpoints"
        emptyDescription="Create an endpoint to deliver outbound webhooks"
        onRowClick={(row) => navigate(`/endpoints/${row.id}`)}
        selectable
        selectedKeys={selectedKeys}
        onSelectionChange={setSelectedKeys}
      />

      <EndpointForm
        open={formOpen}
        onOpenChange={setFormOpen}
        onSuccess={() => reexecute({ requestPolicy: "network-only" })}
      />

      <ConfirmDialog
        open={!!deleteId}
        onOpenChange={(open) => { if (!open) setDeleteId(null) }}
        title="Delete Endpoint"
        description="This will permanently delete this endpoint."
        confirmLabel="Delete"
        onConfirm={handleDelete}
        loading={deleting}
        destructive
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        title="Delete Endpoints"
        description={`This will permanently delete ${selectedKeys.size} selected endpoints.`}
        confirmLabel="Delete"
        onConfirm={handleBulkDelete}
        loading={bulkDeleting}
        destructive
      />
    </div>
  )
}
